import React, { useEffect } from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  return (
    <main className="privacy-page">
      <div className="container privacy-container">
        <header className="privacy-header">
          <h1>Privacy Policy</h1>
          <p className="privacy-subtitle">
            Scan2Card &ndash; AI-Powered Lead Capture &amp; Business Card Scanner
          </p>
          <p className="privacy-meta">
            Scanbiz Technologies LLP | <a href="https://scan2card.com">scan2card.com</a>
          </p>
          <p className="privacy-meta">
            <strong>Last updated:</strong> 03/07/2026
          </p>
        </header>

        <section className="privacy-intro">
          <p>
            <strong>Scan2Card</strong> (&ldquo;Scan2Card&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;,
            &ldquo;our&rdquo;) is a product owned and operated by{' '}
            <strong>Scanbiz Technologies LLP</strong>, a Limited Liability Partnership
            incorporated in India. Scan2Card is an AI-powered lead-capture and
            business-card-scanning platform for exhibitions, expos and trade shows. This
            Privacy Policy explains how we collect, use, store, share and protect personal
            data when you use scan2card.com and the Scan2Card mobile application and services.
            For the purposes of the Digital Personal Data Protection Act, 2023 (&ldquo;DPDP
            Act&rdquo;), Scanbiz Technologies LLP acts as the Data Fiduciary in respect of
            the personal data that it collects and processes directly through the Service.
            Where Users collect personal data of third parties (such as business contacts or
            event visitors) using the Service, such Users are independently responsible for
            complying with applicable data protection laws in relation to such collection and
            processing, and Scanbiz processes such data on behalf of the User only to the
            extent necessary to provide the Service.
          </p>
        </section>

        <section id="section-a" className="privacy-section">
          <h2>A. Who we are and how to contact us</h2>
          <p><strong>Scanbiz Technologies LLP</strong></p>
          <address>
            Office No. 236, 2nd Floor, Adarsh Industrial Estate,<br />
            Sahar Airport Road, Chakala, Andheri (East),<br />
            Mumbai &ndash; 400099, Maharashtra, India
          </address>
          <p><strong>GSTIN:</strong> 27AFUFS5191K1Z1</p>
          <p>
            <strong>Tel.:</strong> +91 72638 89831 &nbsp;|&nbsp;{' '}
            <strong>E-mail:</strong>{' '}
            <a href="mailto:aryan@scan2card.com">aryan@scan2card.com</a>
          </p>
        </section>

        <section id="section-b" className="privacy-section">
          <h2>B. Grievance Officer</h2>
          <p>
            In accordance with applicable laws, including the Digital Personal Data
            Protection Act, 2023 and the Information Technology Act, 2000, you may contact
            our Grievance Officer regarding any questions, requests or complaints relating
            to the processing of your personal data:
          </p>
          <ul>
            <li><strong>Name:</strong> Aryan</li>
            <li>
              <strong>E-mail:</strong>{' '}
              <a href="mailto:aryan@scan2card.com">aryan@scan2card.com</a>
            </li>
            <li>
              <strong>Address:</strong> Office No. 236, 2nd Floor, Adarsh Industrial Estate,
              Sahar Airport Road, Chakala, Andheri (East), Mumbai &ndash; 400099,
              Maharashtra, India
            </li>
          </ul>
          <p>
            We respond within the timelines prescribed by law. If unsatisfied, you may
            approach the Data Protection Board of India. If notified as a &ldquo;Significant
            Data Fiduciary&rdquo;, we will also appoint an India-based Data Protection
            Officer and publish their details here.
          </p>
        </section>

        <section id="section-c" className="privacy-section">
          <h2>C. Notice, consent and legal basis</h2>
          <p>
            We process personal data only for lawful purposes. Where we rely on consent, we
            first give a clear notice of the data sought, the purpose, how to exercise your
            rights, and how to complain to the Data Protection Board of India. By creating
            an account and using features such as card scanning, voice recording, selfie
            capture or calendar sync, you consent to the processing described here. Where
            permitted under the DPDP Act, we may process personal data for certain
            legitimate uses recognised under applicable law, without obtaining separate
            consent where such processing is expressly permitted. You may withdraw consent
            at any time, as easily as it was given, without affecting prior processing.
          </p>
        </section>

        <section id="section-d" className="privacy-section">
          <h2>D. Personal data of website visitors</h2>
          <p>
            When you visit our website, limited usage data is temporarily stored in server
            logs (IP address in shortened form where feasible, date/time, page and HTTP
            status, data volume, referrer, browser and operating system) to operate and
            secure the site. Strictly necessary cookies are used to run the site;
            non-essential cookies only with your consent. See our separate Cookie Policy. If
            you contact us by form, e-mail or WhatsApp, we use your details only to respond,
            retaining them for [12 months] unless law requires longer.
          </p>
        </section>

        <section id="section-e" className="privacy-section">
          <h2>E. Personal data processed within the Scan2Card application</h2>

          <h3>I. Core functionality</h3>
          <p>
            Scan2Card lets exhibitors and teams capture leads by scanning business cards, QR
            codes and digital profiles and converting them into structured digital leads
            using OCR and AI, with multilingual recognition and AI translation, lead
            classification, notes/file attachments, voice notes, visitor selfie capture,
            team-performance tracking, meeting scheduling with calendar sync, and lead
            sharing/export.
          </p>

          <h3>II. Account and team data</h3>
          <p>
            We process your name, e-mail, mobile number, hashed password, organisation/team
            details and other registration fields to provide the service, manage your
            account and team, and produce performance analytics. Retained for the life of
            your account, subject to law.
          </p>

          <h3>III. Scanned cards, QR codes and digital profiles (OCR + AI)</h3>
          <p>
            We capture the card image/QR/profile data and apply OCR and AI to extract
            details such as title, name, company, department, designation, e-mail,
            phone/mobile, postal address, website and other printed text. Multilingual text
            may be sent to an AI translation service for English conversion.
          </p>
          <p>
            <strong>Important:</strong> scanned-card data relates to another individual (the
            visitor). As the exhibitor/user you are the Data Fiduciary for that lead data
            and must have a lawful basis &ndash; such as the visitor&rsquo;s consent at the
            event &ndash; to capture and use it. Scanbiz processes such data solely for the
            purpose of providing and maintaining the Service in accordance with the
            User&rsquo;s instructions and this Privacy Policy and does not determine the
            purposes for which such Lead Data is collected by the User.
          </p>

          <h3>IV. Notes, files, photos and voice recordings</h3>
          <p>
            You may attach notes, files, catalogues and photos to a lead and record
            conversations as voice notes. Recordings and photos are personal data of the
            people involved; you must inform them and obtain their consent before recording
            or photographing. We store these only to provide the service.
          </p>

          <h3>V. Visitor selfie capture</h3>
          <p>
            You can take a selfie with a visitor to help recall the conversation. A photo of
            an identifiable person is personal data; obtain the visitor&rsquo;s consent
            before capturing. Images are stored against the lead, visible only to you and
            your team. We do not run facial-recognition or biometric matching.
          </p>

          <h3>VI. Meeting scheduling and integrations</h3>
          <p>
            If you connect Google Calendar / Google Meet, we process meeting details and the
            minimum account information needed to create and sync events, based on your
            consent and granted permissions. Disconnect anytime in settings to stop syncing.
          </p>

          <h3>VII. Lead sharing, WhatsApp and export</h3>
          <p>
            You can save contacts to your device, share leads on WhatsApp and export to
            Excel. Once you share or export, the data is handled by the destination under
            its own terms; you remain responsible for handling exported personal data
            lawfully.
          </p>

          <h3>VIII. Analytics and team-performance tracking</h3>
          <p>
            We process activity data (e.g. leads scanned per team member, stall engagement
            metrics) to provide dashboards and ROI reports to exhibitors and organisers,
            limited to your organisation&rsquo;s own data.
          </p>

          <h3>IX. Payments</h3>
          <p>
            Payments for paid subscriptions are processed through authorised third-party
            payment service providers. Scanbiz does not collect or store complete payment
            card or banking credentials. Such payment information is processed directly by
            the relevant payment service provider in accordance with its own privacy policy
            and applicable regulatory requirements.
          </p>
        </section>

        <section id="section-f" className="privacy-section">
          <h2>F. Data security, encryption and certifications</h2>
          <p>
            We implement appropriate technical and organisational security measures to
            protect personal data in accordance with applicable laws, including the Digital
            Personal Data Protection Act, 2023, and industry-recognised information security
            standards. Our information security is independently certified:
          </p>
          <ul>
            <li>
              <strong>ISO/IEC 27001:2022</strong> &ndash; Information Security Management
              System (ISMS), Certificate No. SG/IAS/IS/202420, issued by Shamkris Global
              Inspection Services Pvt. Ltd., valid to 29 June 2029.
            </li>
            <li>
              <strong>ISO 9001:2015</strong> &ndash; Quality Management System (QMS),
              Certificate No. SG/IAS/QM/202383, issued by Shamkris Global Inspection
              Services Pvt. Ltd., valid to 29 June 2029.
            </li>
          </ul>
          <p>
            The certified scope covers the protection of information involved in the
            capture, storage and transmission of business-card data through our digital
            platform. Our technical and organisational measures include:
          </p>
          <ul>
            <li>
              <strong>Encryption in transit:</strong> all traffic between the app/website
              and our servers is protected with TLS/HTTPS.
            </li>
            <li>
              <strong>Encryption at rest:</strong> personal data, scanned images,
              attachments and recordings are encrypted at rest using industry-standard
              encryption (e.g. AES-256).
            </li>
            <li>
              <strong>Secure cloud hosting on Amazon Web Services (AWS):</strong> our
              infrastructure runs in AWS data centres [AWS Asia Pacific (Mumbai) region],
              which maintain leading physical and environmental security and their own ISO
              27001, SOC and other certifications. A data processing agreement governs this
              hosting.
            </li>
            <li>
              <strong>Secure authentication:</strong> passwords stored only in hashed and
              salted form; secure login with [optional two-factor authentication].
            </li>
            <li>
              <strong>Role-based access controls</strong> on a need-to-know basis; a
              user&rsquo;s leads are visible only to that user and their authorised team.
            </li>
            <li>
              <strong>Network and application security:</strong> firewalls, logging,
              monitoring and regular security reviews and updates.
            </li>
            <li>
              <strong>Regular encrypted backups</strong> for recovery and continuity.
            </li>
          </ul>
          <p>
            No method of transmission or storage is completely secure, but we protect your
            data using the measures above. In the event of a personal data breach, we will
            notify the Data Protection Board of India and affected Data Principals in the
            manner and within the timelines prescribed under the DPDP Rules, 2025.
          </p>
        </section>

        <section id="section-g" className="privacy-section">
          <h2>G. Data storage, sharing and retention</h2>
          <p>
            Your contacts, leads, attachments and recordings are stored in our AWS cloud
            [hosted in India]. Where data is processed outside India, we do so only to
            countries not restricted by the Central Government and under appropriate
            safeguards. We do not sell personal data. We share it only with Data Processors
            engaged under written contract, including:
          </p>
          <ul>
            <li><strong>Amazon Web Services (AWS)</strong> &ndash; cloud hosting and storage,</li>
            <li>
              <strong>Google (Calendar / Meet)</strong> &ndash; only when you connect the
              integration,
            </li>
            <li>
              <strong>Email / Support / WhatsApp Provider</strong> &ndash; communication.
            </li>
          </ul>
          <p>
            We may disclose data where required by law, court order or a lawful government
            request in India. We retain personal data only for as long as reasonably
            necessary to fulfil the purposes described in this Privacy Policy, to comply
            with applicable legal obligations, resolve disputes, enforce our legal rights or
            satisfy legitimate business requirements. Upon expiry of the applicable
            retention period, personal data will be securely deleted, anonymised or
            otherwise disposed of in accordance with applicable law. You can delete leads,
            attachments, recordings or your whole account anytime in the app or by writing
            to <a href="mailto:aryan@scan2card.com">aryan@scan2card.com</a>.
          </p>
        </section>

        <section id="section-h" className="privacy-section">
          <h2>H. Children&rsquo;s data</h2>
          <p>
            Under the Digital Personal Data Protection Act, 2023, a child means an
            individual who has not completed eighteen (18) years of age. Scan2Card is
            intended solely for use by businesses and professionals and is not directed
            towards children. We do not knowingly process personal data relating to children
            except in accordance with applicable law.
          </p>
        </section>

        <section id="section-i" className="privacy-section">
          <h2>I. Your rights as a Data Principal</h2>
          <p>
            Subject to the DPDP Act, you have the right to access information about your
            data, to correction/completion/updating and erasure, to grievance redressal, to
            nominate another person to exercise your rights, and to withdraw consent. To
            exercise any right, contact{' '}
            <a href="mailto:aryan@scan2card.com">aryan@scan2card.com</a> or our Grievance
            Officer. We may verify your identity first. You must provide accurate
            information and not suppress material information when exercising rights, as
            required by the Act. We may request reasonable information to verify your
            identity before responding to any request relating to your personal data, in
            order to protect your privacy and prevent unauthorised access.
          </p>
        </section>

        <section id="section-j" className="privacy-section">
          <h2>J. Changes and governing law</h2>
          <p>
            We may update this Policy to reflect changes in our services or law; the current
            version is always at scan2card.com and we will notify you of material changes.
            This Privacy Policy shall be governed by and construed in accordance with the
            laws of India, including the Digital Personal Data Protection Act, 2023, the
            rules framed thereunder, the Information Technology Act, 2000 and other
            applicable laws, as amended from time to time.
          </p>
        </section>
      </div>
    </main>
  );
};

export default PrivacyPolicy;
